import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { STAGE_LABELS, STAGE_COLORS, type PipelineStage } from '@/types';
import { cn } from '@/lib/utils';
import type { PipelineApplication } from '@/lib/api';
import { AlertTriangle, ArrowRight } from 'lucide-react';

interface StageChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  application: PipelineApplication | null;
  fromStage: PipelineStage | null;
  toStage: PipelineStage | null;
  onConfirm: (notes: string, disqualificationReason?: string) => void;
  isLoading?: boolean;
}

export function StageChangeDialog({
  open,
  onOpenChange,
  application,
  fromStage,
  toStage,
  onConfirm,
  isLoading,
}: StageChangeDialogProps) {
  const [notes, setNotes] = useState('');
  const [disqualificationReason, setDisqualificationReason] = useState('');

  const isDisqualifying = toStage === 'disqualified';
  const candidate = application?.candidate;

  const handleConfirm = () => {
    onConfirm(notes, isDisqualifying ? disqualificationReason : undefined);
    // Reset form after confirm
    setNotes('');
    setDisqualificationReason('');
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
      setNotes('');
      setDisqualificationReason('');
    }
  };

  if (!application || !fromStage || !toStage) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isDisqualifying ? 'Disqualify Candidate' : 'Move Candidate'}
          </DialogTitle>
          <DialogDescription>
            {isDisqualifying
              ? 'Please provide a reason for disqualifying this candidate.'
              : 'Confirm the stage change for this candidate.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Candidate Info */}
          <div className="bg-bg-secondary rounded-lg p-3">
            <p className="font-medium text-text-primary">
              {candidate?.firstName} {candidate?.lastName}
            </p>
            {candidate?.currentJobTitle && (
              <p className="text-sm text-text-tertiary">{candidate.currentJobTitle}</p>
            )}
          </div>

          {/* Stage Change Visualization */}
          <div className="flex items-center justify-center gap-3 py-2">
            <div className="flex items-center gap-2">
              <div className={cn('w-3 h-3 rounded-full', STAGE_COLORS[fromStage])} />
              <span className="text-sm font-medium">{STAGE_LABELS[fromStage]}</span>
            </div>
            <ArrowRight className="h-4 w-4 text-text-tertiary" />
            <div className="flex items-center gap-2">
              <div className={cn('w-3 h-3 rounded-full', STAGE_COLORS[toStage])} />
              <span className="text-sm font-medium">{STAGE_LABELS[toStage]}</span>
            </div>
          </div>

          {/* Disqualification Warning */}
          {isDisqualifying && (
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">
                <p className="font-medium">This action cannot be easily undone.</p>
                <p className="mt-1">
                  The candidate will be removed from the active pipeline for this job.
                </p>
              </div>
            </div>
          )}

          {/* Disqualification Reason (Required for disqualified) */}
          {isDisqualifying && (
            <div className="space-y-2">
              <Label htmlFor="disqualificationReason">
                Reason for Disqualification <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="disqualificationReason"
                placeholder="Enter the reason for disqualifying this candidate..."
                value={disqualificationReason}
                onChange={(e) => setDisqualificationReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* Notes (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this stage change..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={isDisqualifying ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isLoading || (isDisqualifying && !disqualificationReason.trim())}
          >
            {isLoading
              ? 'Updating...'
              : isDisqualifying
                ? 'Disqualify'
                : 'Confirm Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
